import type { ApiResponse } from '@/types';
import type {
  DeleteInquiryResponse,
  EditInquiryResponse,
  GetDocumentIdsResponse,
  GetDocumentResponse,
  GetInquiriesResponse,
  GetInquiryByIdResponse,
  GetInquiryFilesStatusResponse,
  Inquiry,
  RemoveFileResponse,
  ReplaceSelectionPayload,
  ReplaceSelectionResponse,
} from '@/types/chat/chat';
import type { AddFilesResponse, RemoveResponse } from '@/types/folders/folder';
import { httpServices } from './client';

/** Inquiries (`/llm/chat/inquiry/*`) and their chat uploads (`/llm/chat/document/*`). */

export interface ListInquiriesParams {
  projects_id: string;
}

export interface CreateInquiryRequest {
  name: string;
}

/**
 * `project_id` is sent as a query parameter. The create modal has always
 * sent an empty object here (it reads a prop nobody passes), which axios
 * serializes as `project_id=%7B%7D`; kept as is.
 */
export interface CreateInquiryParams {
  project_id: string | Record<string, never>;
}

export interface RenameInquiryParams {
  name: string;
  project_id?: string | null;
}

export interface UpdateSystemPromptRequest {
  default_prompt: string;
}

export type CreateInquiryResponse = ApiResponse<Inquiry>;

/** GET /llm/chat/inquiry */
export const getInquiries = (params: ListInquiriesParams) =>
  httpServices.llmService.get<GetInquiriesResponse>('/chat/inquiry', params);

/** GET /llm/chat/inquiry/{id} */
export const getInquiry = (inquiryId: string) =>
  httpServices.llmService.get<GetInquiryByIdResponse>(
    `/chat/inquiry/${inquiryId}`,
  );

/** POST /llm/chat/inquiry */
export const createInquiry = (
  body: CreateInquiryRequest,
  params: CreateInquiryParams,
) =>
  httpServices.llmService.post<CreateInquiryResponse>(
    '/chat/inquiry',
    body,
    params,
  );

/** POST /llm/chat/inquiry/{id}/rename?name= (empty body) */
export const renameInquiry = (inquiryId: string, params: RenameInquiryParams) =>
  httpServices.llmService.post<EditInquiryResponse>(
    `/chat/inquiry/${inquiryId}/rename`,
    {},
    params,
  );

/** DELETE /llm/chat/inquiry/{id} */
export const deleteInquiry = (inquiryId: string) =>
  httpServices.llmService.delete<DeleteInquiryResponse>(
    `/chat/inquiry/${inquiryId}`,
  );

/**
 * GET /llm/chat/inquiry/{id}/documents: the document ids of the inquiry's
 * selected folders and files, which are sent along with a question.
 */
export const getInquiryDocumentIds = (inquiryId: string) =>
  httpServices.llmService.get<GetDocumentIdsResponse>(
    `/chat/inquiry/${inquiryId}/documents`,
  );

/**
 * POST /llm/chat/inquiry/{id}/selection: replace the whole file/folder
 * selection. `base_version` is the selection version the payload was
 * derived from; the backend applies the write only while that version is
 * still current.
 */
export const replaceInquirySelection = (
  inquiryId: string,
  payload: ReplaceSelectionPayload,
) =>
  httpServices.llmService.post<ReplaceSelectionResponse>(
    `/chat/inquiry/${inquiryId}/selection`,
    payload,
  );

/** GET /llm/chat/inquiry/files-status/{id} */
export const getInquiryFilesStatus = (inquiryId: string) =>
  httpServices.llmService.get<GetInquiryFilesStatusResponse>(
    `/chat/inquiry/files-status/${inquiryId}`,
  );

/** DELETE /llm/chat/inquiry/{id}/files/{fileId} */
export const removeInquiryFile = (inquiryId: string, fileId: string) =>
  httpServices.llmService.delete<RemoveFileResponse>(
    `/chat/inquiry/${inquiryId}/files/${fileId}`,
  );

/** POST /llm/chat/inquiry/{id}/prompt */
export const updateSystemPrompt = (
  inquiryId: string,
  body: UpdateSystemPromptRequest,
) =>
  httpServices.llmService.post<GetInquiryByIdResponse>(
    `/chat/inquiry/${inquiryId}/prompt`,
    body,
  );

/**
 * POST /llm/chat/inquiry/{id}/attachments: resolve document ids to link
 * targets (name and presigned path). Multipart, one `document_ids` field
 * per id.
 */
export const getDocumentLinks = (inquiryId: string, documentIds: string[]) => {
  const formData = new FormData();
  documentIds.forEach(id => {
    formData.append('document_ids', id);
  });
  return httpServices.llmService.post<GetDocumentResponse>(
    `/chat/inquiry/${inquiryId}/attachments`,
    formData,
    {},
    { 'Content-Type': 'multipart/form-data' },
  );
};

/** POST /llm/chat/document/{id}/add_attachments, multipart `files[]`. */
export const addAttachments = (inquiryId: string, formData: FormData) =>
  httpServices.llmService.post<AddFilesResponse>(
    `/chat/document/${inquiryId}/add_attachments`,
    formData,
    {},
    { 'Content-Type': 'multipart/form-data' },
  );

/** POST /llm/chat/document/{id}/remove_attachments with a JSON array of file ids. */
export const removeAttachments = (inquiryId: string, fileIds: string[]) =>
  httpServices.llmService.post<RemoveResponse>(
    `/chat/document/${inquiryId}/remove_attachments`,
    fileIds,
  );
